import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.message.length < 10) {
      toast.error("Please provide more details about your business needs");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Thank you for your inquiry. We'll be in touch soon.");
      setFormData({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Contact Us
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
            variants={fadeUp}
          >
            Start a Conversation
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            Interested in exploring how Venture Winds can help your business? 
            We welcome inquiries from companies that align with our partnership criteria.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="contact-form-section">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="font-playfair text-2xl md:text-3xl font-semibold text-navy-950 mb-8"
              variants={fadeUp}
            >
              Submit an Inquiry
            </motion.h2>

            {isSubmitted ? (
              <motion.div 
                className="bg-slate-50 p-12 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-playfair text-2xl font-semibold text-navy-950 mb-4">
                  Thank You
                </h3>
                <p className="text-slate-500 mb-6">
                  Your inquiry has been received. Our team will review your 
                  information and reach out if there's potential alignment.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-teal-600 text-sm uppercase tracking-widest font-medium hover:text-teal-700 transition-colors"
                  data-testid="submit-another-btn"
                >
                  Submit Another Inquiry
                </button>
              </motion.div>
            ) : (
              <motion.form 
                onSubmit={handleSubmit}
                className="space-y-8"
                variants={staggerContainer}
                data-testid="contact-form"
              >
                <motion.div variants={fadeUp}>
                  <label className="block text-sm uppercase tracking-widest text-navy-950 font-semibold mb-3">
                    Your Name <span className="text-teal-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-slate-300 px-0 py-4 focus:border-teal-500 focus:outline-none transition-colors"
                    placeholder="John Smith"
                    required
                    data-testid="input-name"
                  />
                </motion.div>

                <motion.div variants={fadeUp}>
                  <label className="block text-sm uppercase tracking-widest text-navy-950 font-semibold mb-3">
                    Company Name <span className="text-teal-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-slate-300 px-0 py-4 focus:border-teal-500 focus:outline-none transition-colors"
                    placeholder="Acme Corporation"
                    required
                    data-testid="input-company"
                  />
                </motion.div>

                <motion.div className="grid md:grid-cols-2 gap-8" variants={fadeUp}>
                  <div>
                    <label className="block text-sm uppercase tracking-widest text-navy-950 font-semibold mb-3">
                      Email <span className="text-teal-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-slate-300 px-0 py-4 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="john@company.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-widest text-navy-950 font-semibold mb-3">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-slate-300 px-0 py-4 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="+1 (234) 567-890"
                      data-testid="input-phone"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <label className="block text-sm uppercase tracking-widest text-navy-950 font-semibold mb-3">
                    Tell Us About Your Business <span className="text-teal-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full bg-transparent border-b border-slate-300 px-0 py-4 focus:border-teal-500 focus:outline-none transition-colors resize-none"
                    placeholder="Describe your business, current challenges, and what you're looking to achieve..."
                    required
                    data-testid="input-message"
                  />
                </motion.div>

                <motion.div variants={fadeUp}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 bg-navy-950 text-white px-10 py-5 text-sm uppercase tracking-widest font-medium btn-shadow hover:bg-teal-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </motion.div>
              </motion.form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="font-playfair text-2xl md:text-3xl font-semibold text-navy-950 mb-8"
              variants={fadeUp}
            >
              Get in Touch
            </motion.h2>

            <motion.div className="space-y-8 mb-12" variants={staggerContainer}>
              <motion.div 
                className="flex items-start gap-4"
                variants={fadeUp}
              >
                <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-950 mb-1">Email</h3>
                  <a 
                    href="mailto:info@venturewinds.com"
                    className="text-slate-500 hover:text-teal-600 transition-colors"
                    data-testid="contact-email"
                  >
                    info@venturewinds.com
                  </a>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-start gap-4"
                variants={fadeUp}
              >
                <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-950 mb-1">Phone</h3>
                  <a 
                    href="tel:+19294449026"
                    className="text-slate-500 hover:text-teal-600 transition-colors"
                    data-testid="contact-phone"
                  >
                    +1 (929) 444-9026
                  </a>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-start gap-4"
                variants={fadeUp}
              >
                <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-950 mb-1">Location</h3>
                  <p className="text-slate-500">
                    United States
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* What to Expect */}
            <motion.div 
              className="bg-slate-50 p-8"
              variants={fadeUp}
            >
              <h3 className="font-playfair text-xl font-semibold text-navy-950 mb-4">
                What to Expect
              </h3>
              <ul className="space-y-3 text-slate-500">
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-semibold">01</span>
                  <span>We review every inquiry personally</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-semibold">02</span>
                  <span>Qualified inquiries receive a response within 5 business days</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-semibold">03</span>
                  <span>Initial conversations are confidential and exploratory</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal-500 font-semibold">04</span>
                  <span>No obligation to proceed at any stage</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-white mb-6"
            variants={fadeUp}
          >
            Not Sure If We're the Right Fit?
          </motion.h2>
          <motion.p 
            className="text-slate-400 text-lg leading-relaxed mb-8"
            variants={fadeUp}
          >
            Review our partnership criteria and approach to determine if there's 
            potential alignment before reaching out.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={fadeUp}>
            <a
              href="/partnerships"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors"
              data-testid="partnerships-link"
            >
              Partnership Criteria
            </a>
            <a
              href="/approach"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors"
              data-testid="approach-link"
            >
              Our Approach
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
